import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useBranch } from "../../../contexts/BranchContext";

const CATEGORIES = [
  { value: "cardio", label: "Cardio" },
  { value: "strength", label: "Strength" },
  { value: "free_weights", label: "Free Weights" },
  { value: "accessories", label: "Accessories" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "operational", label: "Operational", color: "var(--color-success)" },
  { value: "maintenance", label: "Maintenance", color: "#f59e0b" },
  { value: "broken", label: "Broken", color: "var(--color-error)" },
  { value: "retired", label: "Retired", color: "var(--color-muted-foreground)" },
];

const SERVICE_TYPES = [
  { value: "routine", label: "Routine" },
  { value: "repair", label: "Repair" },
  { value: "warranty_claim", label: "Warranty Claim" },
];

function statusColor(status) {
  return STATUSES.find(s => s.value === status)?.color || "var(--color-muted-foreground)";
}

function statusLabel(status) {
  return STATUSES.find(s => s.value === status)?.label || status;
}

function categoryLabel(cat) {
  return CATEGORIES.find(c => c.value === cat)?.label || cat;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr + "T00:00:00") - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Equipment Form Modal ─────────────────────────────────────────────────────

function EquipmentModal({ open, onClose, onSaved, editData, branches, selectedBranchId }) {
  const emptyForm = {
    branch_id: selectedBranchId || "",
    name: "", category: "other", brand: "", model: "", serial_number: "",
    quantity: "1", purchase_date: "", purchase_price: "", warranty_expires: "",
    last_service_date: "", next_service_date: "", status: "operational", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editData) {
      setForm({
        branch_id: editData.branch_id || "",
        name: editData.name || "",
        category: editData.category || "other",
        brand: editData.brand || "",
        model: editData.model || "",
        serial_number: editData.serial_number || "",
        quantity: String(editData.quantity ?? 1),
        purchase_date: editData.purchase_date || "",
        purchase_price: editData.purchase_price ? String(editData.purchase_price) : "",
        warranty_expires: editData.warranty_expires || "",
        last_service_date: editData.last_service_date || "",
        next_service_date: editData.next_service_date || "",
        status: editData.status || "operational",
        notes: editData.notes || "",
      });
    } else {
      setForm({ ...emptyForm, branch_id: selectedBranchId || "" });
    }
    setError(null);
  }, [editData, open, selectedBranchId]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    if (!form.quantity || isNaN(parseInt(form.quantity)) || parseInt(form.quantity) < 1) {
      setError("Quantity must be at least 1."); return;
    }
    setSaving(true);
    try {
      const payload = {
        branch_id: form.branch_id || null,
        name: form.name.trim(),
        category: form.category,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        serial_number: form.serial_number.trim() || null,
        quantity: parseInt(form.quantity),
        purchase_date: form.purchase_date || null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        warranty_expires: form.warranty_expires || null,
        last_service_date: form.last_service_date || null,
        next_service_date: form.next_service_date || null,
        status: form.status,
        notes: form.notes.trim() || null,
      };
      if (editData?.id) {
        await api.put(`/equipment/${editData.id}`, payload);
      } else {
        await api.post("/equipment", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-card)] z-10">
          <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
            {editData ? "Edit Equipment" : "Add Equipment"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <p className="text-xs text-[var(--color-error)]">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {/* Branch */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Branch *</label>
              <select
                value={form.branch_id}
                onChange={e => set("branch_id", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="">All Branches / Unassigned</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {/* Name */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Equipment Name *</label>
              <input
                value={form.name}
                onChange={e => set("name", e.target.value)}
                placeholder="e.g. Treadmill, Barbell Set"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Category</label>
              <select value={form.category} onChange={e => set("category", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {/* Brand */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Brand</label>
              <input value={form.brand} onChange={e => set("brand", e.target.value)} placeholder="e.g. Life Fitness"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Model */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Model</label>
              <input value={form.model} onChange={e => set("model", e.target.value)} placeholder="e.g. T9i"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Serial Number */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Serial Number</label>
              <input value={form.serial_number} onChange={e => set("serial_number", e.target.value)} placeholder="SN-XXXXXXXX"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Quantity */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Quantity</label>
              <input type="number" min="1" value={form.quantity} onChange={e => set("quantity", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Purchase Date */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Purchase Date</label>
              <input type="date" value={form.purchase_date} onChange={e => set("purchase_date", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Purchase Price */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Purchase Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--color-muted-foreground)]">$</span>
                <input type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set("purchase_price", e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
              </div>
            </div>
            {/* Warranty Expires */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Warranty Expires</label>
              <input type="date" value={form.warranty_expires} onChange={e => set("warranty_expires", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Last Service */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Last Service Date</label>
              <input type="date" value={form.last_service_date} onChange={e => set("last_service_date", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Next Service */}
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Next Service Date</label>
              <input type="date" value={form.next_service_date} onChange={e => set("next_service_date", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            {/* Notes */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3}
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] sticky bottom-0 bg-[var(--color-card)]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50">
            {saving ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Check" size={14} color="white" />}
            {saving ? "Saving..." : editData ? "Update" : "Add Equipment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Service Log Modal ────────────────────────────────────────────────────────

function ServiceLogModal({ open, onClose, equipment }) {
  const today = new Date().toISOString().split("T")[0];
  const emptyLog = { service_type: "routine", description: "", cost: "", serviced_by: "", serviced_at: today, next_service_date: "" };
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyLog);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!equipment?.id) return;
    setLoadingLogs(true);
    try {
      const { data } = await api.get(`/equipment/${equipment.id}/service-logs`);
      setLogs(data || []);
    } catch (err) {
      console.error("Failed to load service logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  }, [equipment?.id]);

  useEffect(() => {
    if (open && equipment?.id) {
      fetchLogs();
      setShowForm(false);
      setForm(emptyLog);
      setError(null);
    }
  }, [open, equipment?.id]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSaveLog = async () => {
    if (!form.serviced_at) { setError("Service date is required."); return; }
    setSaving(true);
    try {
      await api.post(`/equipment/${equipment.id}/service-logs`, {
        service_type: form.service_type,
        description: form.description.trim() || null,
        cost: form.cost ? parseFloat(form.cost) : null,
        serviced_by: form.serviced_by.trim() || null,
        serviced_at: form.serviced_at,
        next_service_date: form.next_service_date || null,
      });
      setShowForm(false);
      setForm(emptyLog);
      setError(null);
      fetchLogs();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save log.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Service Log</h3>
            <p className="text-xs text-[var(--color-muted-foreground)]">{equipment?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {!showForm && (
              <button onClick={() => setShowForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors">
                <Icon name="Plus" size={12} color="white" />
                Add Log
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
              <Icon name="X" size={16} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Add Log Form */}
          {showForm && (
            <div className="p-4 bg-[var(--color-muted)]/30 rounded-md border border-[var(--color-border)] space-y-3">
              <p className="text-xs font-semibold text-[var(--color-foreground)]">New Service Record</p>
              {error && (
                <div className="flex items-center gap-2 p-2 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
                  <Icon name="AlertCircle" size={13} color="var(--color-error)" />
                  <p className="text-xs text-[var(--color-error)]">{error}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Type</label>
                  <select value={form.service_type} onChange={e => set("service_type", e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                    {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Date *</label>
                  <input type="date" value={form.serviced_at} onChange={e => set("serviced_at", e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Serviced By</label>
                  <input value={form.serviced_by} onChange={e => set("serviced_by", e.target.value)} placeholder="Technician name"
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Cost</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--color-muted-foreground)]">$</span>
                    <input type="number" min="0" step="0.01" value={form.cost} onChange={e => set("cost", e.target.value)} placeholder="0.00"
                      className="w-full pl-6 pr-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Next Service</label>
                  <input type="date" value={form.next_service_date} onChange={e => set("next_service_date", e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Description</label>
                  <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Service details..."
                    className="w-full px-2.5 py-1.5 text-sm border border-[var(--color-border)] rounded bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => { setShowForm(false); setError(null); }}
                  className="px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
                <button onClick={handleSaveLog} disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50">
                  {saving ? <Icon name="Loader2" size={12} color="white" className="animate-spin" /> : <Icon name="Check" size={12} color="white" />}
                  {saving ? "Saving..." : "Save Log"}
                </button>
              </div>
            </div>
          )}

          {/* Log History */}
          {loadingLogs ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-[var(--color-muted)] rounded animate-pulse" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <div className="w-10 h-10 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                <Icon name="ClipboardList" size={18} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)]">No service records yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map(log => (
                <div key={log.id} className="p-3 bg-[var(--color-muted)]/20 rounded-md border border-[var(--color-border)]">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: "var(--color-primary)18", color: "var(--color-primary)" }}>
                        {SERVICE_TYPES.find(t => t.value === log.service_type)?.label || log.service_type}
                      </span>
                      {log.cost && (
                        <span className="text-xs font-medium text-[var(--color-foreground)]">
                          ${parseFloat(log.cost).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-[var(--color-muted-foreground)] whitespace-nowrap">{formatDate(log.serviced_at)}</span>
                  </div>
                  {log.serviced_by && <p className="text-xs text-[var(--color-muted-foreground)]">By: {log.serviced_by}</p>}
                  {log.description && <p className="text-xs text-[var(--color-foreground)] mt-1">{log.description}</p>}
                  {log.next_service_date && (
                    <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Next: {formatDate(log.next_service_date)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EquipmentInventory() {
  const { selectedBranchId, branches } = useBranch();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [serviceTarget, setServiceTarget] = useState(null);

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get("/equipment", { params });
      setEquipment(data || []);
    } catch (err) {
      console.error("Failed to load equipment:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/equipment/${deleteId}`);
      setDeleteId(null);
      fetchEquipment();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  // Maintenance alerts
  const alerts = equipment.filter(e => {
    const serviceIn7 = e.next_service_date && daysUntil(e.next_service_date) !== null && daysUntil(e.next_service_date) <= 7;
    const warrantyIn30 = e.warranty_expires && daysUntil(e.warranty_expires) !== null && daysUntil(e.warranty_expires) <= 30 && daysUntil(e.warranty_expires) >= 0;
    const isBroken = e.status === "broken";
    return serviceIn7 || warrantyIn30 || isBroken;
  });

  const filtered = equipment.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || e.name?.toLowerCase().includes(q) || e.brand?.toLowerCase().includes(q) || e.model?.toLowerCase().includes(q) || e.serial_number?.toLowerCase().includes(q);
    const matchCat = !filterCategory || e.category === filterCategory;
    const matchStatus = !filterStatus || e.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  // Stats
  const total = equipment.length;
  const operational = equipment.filter(e => e.status === "operational").length;
  const needsAttention = equipment.filter(e => ["maintenance", "broken"].includes(e.status)).length;
  const maintenanceDue = equipment.filter(e => e.next_service_date && daysUntil(e.next_service_date) !== null && daysUntil(e.next_service_date) <= 7).length;

  const hasFilters = search || filterCategory || filterStatus;

  return (
    <div className="space-y-5">
      {/* Stats Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Equipment", value: loading ? "..." : String(total), icon: "Dumbbell", color: "var(--color-primary)" },
          { label: "Operational", value: loading ? "..." : String(operational), icon: "CheckCircle2", color: "var(--color-success)" },
          { label: "Needs Attention", value: loading ? "..." : String(needsAttention), icon: "AlertTriangle", color: "var(--color-warning)" },
          { label: "Service Due (7d)", value: loading ? "..." : String(maintenanceDue), icon: "Wrench", color: "#f59e0b" },
        ].map((stat, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)]">
            <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${stat.color}18` }}>
              <Icon name={stat.icon} size={16} color={stat.color} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate">{stat.label}</p>
              <p className="text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Maintenance Alerts */}
      {!loading && alerts.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-[var(--radius-sm)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={15} color="#f59e0b" />
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Attention Required ({alerts.length})</p>
          </div>
          <div className="space-y-2">
            {alerts.map(e => {
              const svcDays = e.next_service_date ? daysUntil(e.next_service_date) : null;
              const warDays = e.warranty_expires ? daysUntil(e.warranty_expires) : null;
              return (
                <div key={e.id} className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor(e.status) }} />
                    <span className="font-medium text-[var(--color-foreground)] truncate">{e.name}</span>
                    {e.branch_name && <span className="text-xs text-[var(--color-muted-foreground)] hidden sm:inline">{e.branch_name}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                    {e.status === "broken" && (
                      <span className="px-2 py-0.5 rounded bg-[var(--color-error)]/10 text-[var(--color-error)] font-medium">Broken</span>
                    )}
                    {svcDays !== null && svcDays <= 7 && (
                      <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
                        Service {svcDays <= 0 ? "overdue" : `in ${svcDays}d`}
                      </span>
                    )}
                    {warDays !== null && warDays <= 30 && warDays >= 0 && (
                      <span className="px-2 py-0.5 rounded bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium">
                        Warranty in {warDays}d
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 min-w-0">
            <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, brand, model, serial..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(""); setFilterCategory(""); setFilterStatus(""); }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors whitespace-nowrap">
              <Icon name="X" size={12} color="var(--color-muted-foreground)" />
              Clear
            </button>
          )}
        </div>
        <button onClick={() => { setEditData(null); setModalOpen(true); }}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors whitespace-nowrap">
          <Icon name="Plus" size={14} color="white" />
          Add Equipment
        </button>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Branch</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[var(--color-muted-foreground)]">Qty</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)] whitespace-nowrap">Next Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)] whitespace-nowrap">Warranty</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[var(--color-muted-foreground)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)]">
                    {[...Array(8)].map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-[var(--color-muted)] rounded animate-pulse" style={{ width: j === 7 ? "60px" : "80px" }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                        <Icon name="Dumbbell" size={22} color="var(--color-muted-foreground)" />
                      </div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        {hasFilters ? "No equipment matches your filters" : "No equipment added yet"}
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">
                        {hasFilters ? "Try adjusting your filters" : "Click 'Add Equipment' to start tracking your gym assets"}
                      </p>
                      {!hasFilters && (
                        <button onClick={() => { setEditData(null); setModalOpen(true); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors">
                          <Icon name="Plus" size={12} color="white" />
                          Add Equipment
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(item => {
                  const svcDays = item.next_service_date ? daysUntil(item.next_service_date) : null;
                  const warDays = item.warranty_expires ? daysUntil(item.warranty_expires) : null;
                  return (
                    <tr key={item.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-[var(--color-foreground)]">{item.name}</p>
                          {(item.brand || item.model) && (
                            <p className="text-xs text-[var(--color-muted-foreground)]">
                              {[item.brand, item.model].filter(Boolean).join(" · ")}
                            </p>
                          )}
                          {item.serial_number && (
                            <p className="text-xs font-[var(--font-data)] text-[var(--color-muted-foreground)]">{item.serial_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                          {categoryLabel(item.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)]">
                        {item.branch_name || <span className="text-[var(--color-muted-foreground)]/50">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: `${statusColor(item.status)}18`, color: statusColor(item.status) }}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor(item.status) }} />
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-medium text-[var(--color-foreground)]">{item.quantity}</span>
                      </td>
                      <td className="px-4 py-3">
                        {item.next_service_date ? (
                          <span className={`text-xs font-medium ${svcDays !== null && svcDays <= 0 ? "text-[var(--color-error)]" : svcDays !== null && svcDays <= 7 ? "text-amber-500" : "text-[var(--color-muted-foreground)]"}`}>
                            {formatDate(item.next_service_date)}
                          </span>
                        ) : <span className="text-xs text-[var(--color-muted-foreground)]/50">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {item.warranty_expires ? (
                          <span className={`text-xs font-medium ${warDays !== null && warDays <= 0 ? "text-[var(--color-error)]" : warDays !== null && warDays <= 30 ? "text-[var(--color-primary)]" : "text-[var(--color-muted-foreground)]"}`}>
                            {formatDate(item.warranty_expires)}
                          </span>
                        ) : <span className="text-xs text-[var(--color-muted-foreground)]/50">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setServiceTarget(item)} title="Service Log"
                            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
                            <Icon name="ClipboardList" size={13} color="var(--color-muted-foreground)" />
                          </button>
                          <button onClick={() => { setEditData(item); setModalOpen(true); }} title="Edit"
                            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
                            <Icon name="Pencil" size={13} color="var(--color-muted-foreground)" />
                          </button>
                          <button onClick={() => setDeleteId(item.id)} title="Delete"
                            className="p-1.5 rounded-md hover:bg-[var(--color-error)]/10 transition-colors">
                            <Icon name="Trash2" size={13} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2.5 border-t border-[var(--color-border)] bg-[var(--color-muted)]/20">
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {filtered.length} item{filtered.length !== 1 ? "s" : ""}
              {filtered.length !== equipment.length && ` (filtered from ${equipment.length})`}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <EquipmentModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditData(null); }}
        onSaved={fetchEquipment}
        editData={editData}
        branches={branches}
        selectedBranchId={selectedBranchId}
      />

      <ServiceLogModal
        open={!!serviceTarget}
        onClose={() => setServiceTarget(null)}
        equipment={serviceTarget}
      />

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="Trash2" size={18} color="var(--color-error)" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[var(--color-foreground)]">Delete Equipment</h4>
                <p className="text-xs text-[var(--color-muted-foreground)]">This will also delete all service logs.</p>
              </div>
            </div>
            <p className="text-sm text-[var(--color-muted-foreground)] mb-5">Are you sure you want to delete this equipment record?</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-error)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50">
                {deleting ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Trash2" size={14} color="white" />}
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
